import type { AttendanceMonthly } from '../types';

/**
 * One row per person, counting how their month went.
 *
 * Deliberately no "attendance percentage" column. The denominator is the
 * argument, not the arithmetic — whether a half day counts as half, whether an
 * unmonitored day counts at all, whether pending days belong in it — and a
 * single number on a screen settles that silently and then gets quoted in a
 * meeting. Scheduled is shown beside the counts so whoever needs a ratio picks
 * their own and knows what they picked.
 *
 * Zeroes render as a dash. A column of noughts is harder to read past than one
 * with the empty cells actually empty.
 */
function Cell({ value, tone }: { value: number; tone?: string }) {
  return (
    <td className={`px-3 py-2.5 text-right tabular-nums ${tone ?? ''}`}>
      {value === 0 ? <span className="text-gray-300">—</span> : value}
    </td>
  );
}

function hours(minutes: number): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export default function AttendanceMonthlyTable({
  rows,
  isLoading,
}: {
  rows: AttendanceMonthly[];
  isLoading: boolean;
}) {
  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-4 py-2.5 font-medium">Name</th>
            <th className="px-3 py-2.5 text-right font-medium">Scheduled</th>
            <th className="px-3 py-2.5 text-right font-medium">Present</th>
            <th className="px-3 py-2.5 text-right font-medium">Late</th>
            <th className="px-3 py-2.5 text-right font-medium">Half day</th>
            <th className="px-3 py-2.5 text-right font-medium">Absent</th>
            <th className="px-3 py-2.5 text-right font-medium">Leave</th>
            <th className="px-3 py-2.5 text-right font-medium">Off</th>
            {/* Its own column rather than folded into Absent: a day nobody
                could be measured on is not a day somebody missed. */}
            <th className="px-3 py-2.5 text-right font-medium">Unmonitored</th>
            <th className="px-3 py-2.5 text-right font-medium">Worked</th>
            {/* The overrun count, which is the whole reason it is a column and
                not a note on one day's detail: one long lunch is a day, the
                same lunch every day is a pattern, and only a month of them
                side by side shows which this is. */}
            <th className="px-3 py-2.5 text-right font-medium">Over break</th>
            <th className="px-3 py-2.5 text-right font-medium">Avg avail.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                Loading…
              </td>
            </tr>
          )}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={12} className="px-4 py-8 text-center text-gray-400">
                Nothing for this month. A month that has not started yet shows
                nothing at all.
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row.employeeId} className="hover:bg-gray-50">
              <td className="px-4 py-2.5">
                <span className="font-medium text-gray-900">
                  {row.userName ?? row.employeeCode}
                </span>
                <span className="block text-xs text-gray-500">
                  {row.designation ?? row.employeeCode}
                  {row.noShiftAssigned > 0 && (
                    // Said rather than left to be inferred from a row of
                    // dashes: without a policy there is no assigned start, so
                    // there is nothing to be absent from.
                    <span className="text-amber-600">
                      {' '}· {row.noShiftAssigned}d without a shift policy
                    </span>
                  )}
                </span>
              </td>
              <Cell value={row.scheduledDays} tone="text-gray-500" />
              <Cell value={row.present} tone="text-emerald-700" />
              <Cell value={row.late} tone="text-amber-700" />
              <Cell value={row.halfDay} tone="text-amber-700" />
              <Cell value={row.absent} tone="text-red-700" />
              <Cell value={row.leave} tone="text-gray-600" />
              <Cell value={row.weeklyOff + row.holiday} tone="text-gray-400" />
              <Cell value={row.unmonitored} tone="text-gray-500" />
              <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                {hours(row.workedMinutes)}
              </td>
              <Cell value={row.breakOverruns} tone="text-amber-700" />
              <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                {/* Null, not zero, where no day was scored — an average of
                    nothing is not nought per cent. */}
                {row.avgAvailabilityPct == null
                  ? <span className="text-gray-300">—</span>
                  : `${row.avgAvailabilityPct}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
